import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import TextField from "../../Form/TextField";
import TextArea from "../../Form/TextArea";
import { useTranslation } from "react-i18next";
import NewModal from "../../shared/Modals/NewModal";
import FileDropzone from "../../shared/Modals/FileDropzone";
import { useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import TabTitle from "../../shared/Tabs/TabTitle";
import crudApi from "@serie3/common/api/crudApi";
import LoadingModal from "../../shared/Modals/LoadingModal";
import { faUpload, faWandMagicSparkles, faCheck, faTimes, faFont, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useUploadSummary } from "../../shared/Upload/useUploads";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LinkedinPreview from "./Previews/LinkedinPreview";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import FunnelStageSelect from "../../Form/FunnelStageSelect";
import NewButtonWithIcon from "../../shared/Buttons/NewButtonWithIcon";
import Filemanager from "./Files/Filemanager";
import { useEffect, useRef } from "react";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DragableFileListItem from "./Filemanager/DragableFileListItem";
import AIDropdownMenu from "./SubComponents/AIDropdownMenu";
import listHandler from "@serie3/common/Domain/listHandler";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import PollFormView from "./Poll/PollFormView";
import ToastMessages from "../../../ToastMessages";
import Poll from "@serie3/mymediaplanner/Modules/Social/Entity/Poll";
import ContentPillarSelect from "../../Form/ContentPillarSelect";
import OverlayEditorModal from "./Overlay/OverlayEditorModal";
import overlayApi from "@serie3/mymediaplanner/Modules/Social/Domain/overlayApi";
import {
  normalizeOverlay,
  setOverlayEnabled,
  isImageDocument,
} from "@serie3/mymediaplanner/Modules/Social/Domain/overlayRenderer";

/**
 * `group`: set when the post is edited from a group's board. Files added
 * then go to the group's file folder (POST groups/content/postfiles) instead
 * of the uploader's personal files; the connection to the post is the same.
 */
const ComposePostTab = ({ formik, editData, pillarsEndpoint = null, group = null }) => {
  const { t } = useTranslation();
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showFileManager, setShowFilemanager] = useState(false);
  const [postFiles, setPostFiles] = useState([]);
  const uploadScope = `post-${editData.id}`;
  const uploadSummary = useUploadSummary(uploadScope);
  const [overlayConnection, setOverlayConnection] = useState(null);
  const [togglingOverlay, setTogglingOverlay] = useState(false);
  const cursorPositionRef = useRef(0);
  const [textCheckResult, setTextCheckResult] = useState({});
  const [showTextChecker, setShowTextChecker] = useState(false);
  const [appliedChanges, setAppliedChanges] = useState(new Set());
  const [ctaHookResult, setCtaHooKResult] = useState({});
  const [showCtaHookResult, setShowCtaHookResult] = useState(false);
  const [poll, setPoll] = useState(editData.poll);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [existingTags, setExistingTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMeta, setShowMeta] = useState(true);

  // Local text state so typing only triggers a cheap local re-render instead of
  // a full formik re-render of the entire ComposePostTab tree on every keystroke.
  const [localText, setLocalText] = useState(formik.values.text ?? "");
  const syncRef = useRef(null);
  // Marks formik updates that we ourselves dispatched from typing, so the
  // sync-back effect below doesn't overwrite freshly typed characters mid-flight.
  const selfSyncingRef = useRef(false);

  // Keep local text in sync when formik.values.text is changed externally
  // (e.g. AI suggestions applied, text checker changes).
  useEffect(() => {
    if (selfSyncingRef.current) {
      selfSyncingRef.current = false;
      return;
    }
    // External change wins — drop any pending typing-debounce that would otherwise
    // stomp on it once it fires.
    clearTimeout(syncRef.current);
    setLocalText(formik.values.text ?? "");
  }, [formik.values.text]);

  // Debounce the preview so LinkedinPreview only re-renders after typing pauses.
  const [previewText, setPreviewText] = useState(formik.values.text);
  useEffect(() => {
    const timer = setTimeout(() => setPreviewText(localText), 400);
    return () => clearTimeout(timer);
  }, [localText]);

  const onTextChange = (e) => {
    const val = e.target.value;
    setLocalText(val);
    // Sync to formik after 300ms — keeps autosave and form submission up to date
    // without triggering a formik re-render on every keystroke.
    clearTimeout(syncRef.current);
    syncRef.current = setTimeout(() => {
      selfSyncingRef.current = true;
      formik.setFieldValue("text", val);
    }, 300);
  };

  const trackCursor = (e) => { cursorPositionRef.current = e.target.selectionStart; };

  useEffect(() => {
    const loadPostDocuments = async () => {
      const response = await crudApi.getAll(
        DocumentsEndpoints.showPostFiles + editData.id
      );
      if (response.ok) {
        setPostFiles(response.data);
      }
    };
    const loadExistingTags = async () => {
      const response = await crudApi.justGet(SocialEndpoints.posts.tags.list);
      if (response?.ok) {
        const list = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
        setExistingTags(list);
      }
    };
    loadPostDocuments();
    loadExistingTags();
  }, [editData.id]);

  const moveRow = (dragIndex, hoverIndex) => {
    const updatedFiles = [...postFiles];
    const [draggedFile] = updatedFiles.splice(dragIndex, 1); // Remove dragged item
    updatedFiles.splice(hoverIndex, 0, draggedFile); // Insert dragged item in new position
    setPostFiles(updatedFiles);
  };

  const dropFunction = (dragedItem) => {
    let index = dragedItem.index;
    const previousFile = postFiles[index - 1] || null; // Get the file before (or null if it's the first)
    const movedElement = postFiles[index]; // The dropped file
    const afterFile = postFiles[index + 1] || null;

    const data = {};
    if (previousFile !== null) {
      data.before = previousFile.id;
    }
    if (afterFile !== null) {
      data.after = afterFile.id;
    }
    data.moved_element = movedElement.id;

    crudApi.createNew(DocumentsEndpoints.sort, data);
  };


  const connectFileWithPost = async (file) => {
    const itemIndex = postFiles.findIndex((obj) => obj.document.id === file.id);
    if (itemIndex > -1) {
      ToastMessages.NotAllowed("File already connected to post");
    } else {
      const response = await crudApi.justGet(
        DocumentsEndpoints.connectFileToPost + editData.id + "/" + file.id
      );
      if (response.ok) {
        listHandler.newToList(response, postFiles, setPostFiles);
        ToastMessages.SuccessfulSave();
      } else {
        ToastMessages.NotAllowed();
      }
    }
  };

  /** Images are the only thing a title can be drawn on. */
  const imageConnections = postFiles.filter((connection) =>
    isImageDocument(connection.document)
  );

  /**
   * On by default: an image nobody has touched has a NULL overlay, and NULL
   * normalises to enabled, so the toggle reads on and the title is drawn.
   */
  const titleOverlayOn =
    imageConnections.length > 0 &&
    imageConnections.every((connection) => normalizeOverlay(connection.overlay).enabled);

  /** Switches the title on or off for every image on this post. */
  const toggleTitleOverlay = async () => {
    if (imageConnections.length === 0 || togglingOverlay) {
      return;
    }

    setTogglingOverlay(true);
    const next = !titleOverlayOn;
    const updated = {};

    for (const connection of imageConnections) {
      // Only the flag changes; the position and CSS are kept so switching back
      // restores exactly what was there.
      const overlay = setOverlayEnabled(connection.overlay, next);
      await overlayApi.saveOverlay(connection.id, overlay);
      updated[connection.id] = overlay;
    }

    setPostFiles((previous) =>
      previous.map((entry) =>
        updated[entry.id] ? { ...entry, overlay: updated[entry.id] } : entry
      )
    );
    setTogglingOverlay(false);
  };

  /** Mirrors the editor's autosave into the local list (preview + overlay marker). */
  const handleOverlayChange = (connectionId, overlay) => {
    setPostFiles((previous) =>
      previous.map((entry) =>
        entry.id === connectionId ? { ...entry, overlay } : entry
      )
    );
    setOverlayConnection((previous) =>
      previous && previous.id === connectionId
        ? { ...previous, overlay }
        : previous
    );
  };

  const removeFilesFromPost = async (connection) => {
    const itemIndex = postFiles.findIndex((obj) => obj.id === connection.id);
    listHandler.deleteFromList(
      itemIndex,
      crudApi.del(DocumentsEndpoints.removeFileFromPost, connection.id),
      setPostFiles,
      postFiles
    );
  };

  const addPoll = async () => {
    const response = await crudApi.createNew(
      SocialEndpoints.posts.poll.new + editData.id,
      {}
    );
    if (response.ok) {
      console.log(response.data);
      let tmpPoll = { ...Poll };
      tmpPoll.id = response.data.id;
      setPoll(tmpPoll);
      editData.poll = { ...tmpPoll };
    }
  };

  const deletePoll = async () => {
    const response = await crudApi.del(
      SocialEndpoints.posts.poll.delete,
      editData.poll.id
    );
    if (response.ok) {
      setPoll(null);
      editData.poll = null;
    }
  };

  const currentTags = formik.values.tags ?? [];

  const addTag = (input) => {
    const names = input.split(",").map((s) => s.trim()).filter(Boolean);
    if (!names.length) return;
    const existing = new Set(currentTags.map((t) => t.name?.toLowerCase()));
    const next = [...currentTags, ...names.filter((n) => !existing.has(n.toLowerCase())).map((n) => ({ name: n }))];
    formik.setFieldValue("tags", next);
    setTagInput("");
    setShowSuggestions(false);
  };

  const removeTag = (tag) => {
    formik.setFieldValue("tags", currentTags.filter((t) => t.name !== tag.name));
  };

  const suggestions = tagInput.trim()
    ? existingTags.filter(
        (t) =>
          t.name?.toLowerCase().includes(tagInput.trim().toLowerCase()) &&
          !currentTags.some((ct) => ct.name?.toLowerCase() === t.name?.toLowerCase())
      )
    : [];

  return (
    <>
      <NewModal
        size="xl"
        show={showTextChecker}
        setShow={setShowTextChecker}
        title={t("Backend.Post.CheckTextHeader")}
        onExit={() => setAppliedChanges(new Set())}
      >
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
            {textCheckResult.changes && textCheckResult.changes.map((change, index) => (
              <div key={index} style={{
                borderRadius: 12, border: "1px solid #eaecf0",
                background: "#fff", overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 16px", borderBottom: "1px solid #f0f2f5",
                  background: "#f8fafc",
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                    background: "#038edc", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700,
                  }}>{index + 1}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "#6b7280" }}>
                    {change.type}
                  </span>
                </div>
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: "#f34e4e", background: "rgba(243,78,78,0.07)", padding: "4px 10px", borderRadius: 6, fontStyle: "italic" }}>
                      {change.original}
                    </span>
                    <span style={{ color: "#9299a8", fontSize: 16, lineHeight: "26px" }}>→</span>
                    <span style={{ fontSize: 13, color: "#51d28c", background: "rgba(81,210,140,0.09)", padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
                      {change.change}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 600, color: "#374151", marginRight: 4 }}>{t("Backend.Post.ChangingReason")}:</span>
                    {change.reason}
                  </p>
                  <button
                    type="button"
                    disabled={appliedChanges.has(index)}
                    onClick={() => {
                      const updatedText = formik.values.text.replace(change.original, change.change);
                      formik.setFieldValue("text", updatedText);
                      setAppliedChanges(prev => new Set(prev).add(index));
                    }}
                    style={{
                      padding: "6px 16px", borderRadius: 50, border: "none",
                      background: appliedChanges.has(index)
                        ? "#e2e5ea"
                        : "linear-gradient(135deg,#038edc,#0270b8)",
                      color: appliedChanges.has(index) ? "#9299a8" : "#fff",
                      fontSize: 12, fontWeight: 600,
                      cursor: appliedChanges.has(index) ? "default" : "pointer",
                      boxShadow: appliedChanges.has(index) ? "none" : "0 3px 10px rgba(3,142,220,0.25)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {appliedChanges.has(index) ? "✓ Applied" : t("Backend.Post.ApplyChange")}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div style={{
            flex: 1, padding: "16px 20px", borderRadius: 12,
            background: "#f8fafc", border: "1px solid #eaecf0",
            fontSize: 14, lineHeight: 1.8, color: "#374151",
            whiteSpace: "pre-wrap", maxHeight: 500, overflowY: "auto",
          }}
          >
            {formik.values.text}
          </div>
        </div>
      </NewModal>

      <NewModal
        size="xl"
        show={showCtaHookResult}
        setShow={setShowCtaHookResult}
        title={t("Backend.Post.HooksAndCta")}
      >
        {(() => {
          const type = "hooks" in ctaHookResult ? "hooks" : "cta";
          const options = type === "hooks" ? ctaHookResult.hooks : ctaHookResult.cta;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {options?.map((response, index) => (
                <div key={index} style={{
                  borderRadius: 12, border: "1px solid #eaecf0",
                  background: "#fff", padding: "16px 20px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  display: "flex", alignItems: "flex-start", gap: 14,
                }}>
                  <span style={{
                    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                    background: type === "hooks" ? "rgba(3,142,220,0.1)" : "rgba(241,115,79,0.1)",
                    color: type === "hooks" ? "#038edc" : "#f1734f",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, marginTop: 1,
                  }}>{index + 1}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: "0 0 12px" }}>
                      {response.text}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedText = type === "hooks"
                          ? response.text + "\n\n\n" + formik.values.text
                          : formik.values.text + "\n\n\n" + response.text;
                        formik.setFieldValue("text", updatedText);
                        setShowCtaHookResult(false);
                      }}
                      style={{
                        padding: "6px 16px", borderRadius: 50, border: "none",
                        background: type === "hooks"
                          ? "linear-gradient(135deg,#038edc,#0270b8)"
                          : "linear-gradient(135deg,#f1734f,#d45a38)",
                        color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
                        boxShadow: type === "hooks"
                          ? "0 3px 10px rgba(3,142,220,0.25)"
                          : "0 3px 10px rgba(241,115,79,0.25)",
                      }}
                    >
                      {t("Backend.Post.ApplyChange")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </NewModal>

      <OverlayEditorModal
        show={Boolean(overlayConnection)}
        setShow={() => setOverlayConnection(null)}
        connection={overlayConnection}
        title={formik.values.title}
        onOverlayChange={handleOverlayChange}
      />

      <NewModal size="xl" show={showFileManager} setShow={setShowFilemanager}>
        <Filemanager handleFileFunction={connectFileWithPost} useGrid={true} showAdd={true}/>
      </NewModal>

      <NewModal show={showFileUpload} setShow={setShowFileUpload}>
        <FileDropzone
          scope={uploadScope}
          uploadUrl={
            group?.id
              ? MediaplannerGroupEndpoints.endpoints.postFiles + group.id + "/" + editData.id
              : DocumentsEndpoints.addToPost + editData.id
          }
          setName={false}
          // fires per finished file, possibly after the modal was closed or the list reordered
          onUploaded={(connection) =>
            setPostFiles((prev) =>
              prev.some((entry) => entry.id === connection.id) ? prev : [...prev, connection]
            )
          }
          edit={false}
          additionalData={{}}
        />
      </NewModal>
      <LoadingModal
        showSpinner={showLoadingModal}
        setShowSpinner={setShowLoadingModal}
        text={t("Backend.Post.AIworking")}
      />

      <Row>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Tabs
                defaultActiveKey="write"
                id="uncontrolled-tab-example"
                className="mb-3"
              >
                <Tab
                  eventKey="write"
                  title={<TabTitle icon="uil-align-left" label={t("Backend.Post.Tabs.Text")} />}
                >
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                    <button
                      type="button"
                      onClick={() => setShowMeta((v) => !v)}
                      style={{
                        background: "none", border: "none", padding: 0,
                        fontSize: 12, fontWeight: 600,
                        color: "#038edc", cursor: "pointer",
                      }}
                    >
                      {showMeta ? "▾ Hide details" : "▸ Show details"}
                    </button>
                  </div>
                  {showMeta && (
                    <>
                      <Row>
                        <Col>
                          {/* Label above, input + toggle centred in their own row: the theme's
                              input is 49px tall, so bottom-aligning next to label+input put
                              the 38px button 5.5px below the input's centre. */}
                          <Form.Label htmlFor="post-title">{t("Backend.Post.Title")}</Form.Label>
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ marginBottom: "10px" }}
                          >
                            <div className="flex-grow-1">
                              <TextField
                                formik={formik}
                                id="post-title"
                                valueName={"title"}
                              />
                            </div>
                            {(() => {
                              const toggleHint =
                                imageConnections.length === 0
                                  ? t("Backend.Overlay.ToggleNoImage", "Attach an image first")
                                  : titleOverlayOn
                                  ? t("Backend.Overlay.ToggleOff", "Title is on the image — click to hide it")
                                  : t("Backend.Overlay.ToggleOn", "Show the title on the image");
                              return (
                                // The tooltip sits on a wrapper: browsers show no title
                                // tooltip on a disabled button.
                                <span className="d-inline-flex" title={toggleHint}>
                                  <button
                                    type="button"
                                    id="title-on-image"
                                    className={`iconToggle${titleOverlayOn ? " isOn" : ""}`}
                                    aria-pressed={titleOverlayOn}
                                    aria-label={toggleHint}
                                    disabled={
                                      imageConnections.length === 0 || togglingOverlay
                                    }
                                    onClick={toggleTitleOverlay}
                                  >
                                    <FontAwesomeIcon icon={faFont} />
                                  </button>
                                </span>
                              );
                            })()}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={8}>
                          <ContentPillarSelect formik={formik} pillarsEndpoint={pillarsEndpoint} />
                        </Col>
                        <Col md={4}>
                          <FunnelStageSelect formik={formik} />
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col>
                          <div style={{ position: "relative" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <Form.Control
                                type="text"
                                value={tagInput}
                                placeholder="Add tag…"
                                onChange={(e) => { setTagInput(e.target.value); setShowSuggestions(true); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                              />
                              <Button variant="outline-primary" onClick={() => addTag(tagInput)}>+</Button>
                            </div>
                            {showSuggestions && suggestions.length > 0 && (
                              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "#fff", border: "1px solid #e0e4ea", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", marginTop: 2 }}>
                                {suggestions.map((t) => (
                                  <div
                                    key={t.name}
                                    onMouseDown={() => addTag(t.name)}
                                    style={{ padding: "6px 12px", fontSize: 12, cursor: "pointer", color: "#374151" }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                  >
                                    {t.name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {currentTags.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                              {currentTags.map((tag) => (
                                <span key={tag.name} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#e8f4fd", color: "#038edc", border: "1px solid #c8e6f9" }}>
                                  {tag.name}
                                  <span onClick={() => removeTag(tag)} style={{ cursor: "pointer", fontSize: 10, color: "#6b7280", lineHeight: 1 }}>✕</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                  <Row className="mt-3">
                    <Col className="mt-3">
                      <TextArea
                        value={localText}
                        onChange={onTextChange}
                        label=" "
                        valueName={"text"}
                        style={{ marginBottom: "10px" }}
                        rows={35}
                        onClick={trackCursor}
                        onKeyUp={trackCursor}
                        onSelect={trackCursor}
                        LabelButton={() => (
                          <AIDropdownMenu
                            charCount={localText.length}
                            formik={formik}
                            setShowLoadingModal={setShowLoadingModal}
                            setAiPreview={setAiPreview}
                            setAiLoading={setAiLoading}
                            cursorPositionRef={cursorPositionRef}
                            editData={editData}
                            setTextCheckResult={setTextCheckResult}
                            setShowTextChecker={setShowTextChecker}
                            setCtaHooKResult={setCtaHooKResult}
                            setShowCtaHookResult={setShowCtaHookResult}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                </Tab>
                {(formik.values.post_type === "ShortVideo" || formik.values.post_type === "Video") && (
                  <Tab
                    eventKey="shotlist"
                    title={<TabTitle icon="uil-list-ul" label={t("Backend.Post.Tabs.Shotlist", "Shotlist")} />}
                  >
                    <div data-color-mode="light" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      <MDEditor
                        value={formik.values.shotlist ?? ""}
                        onChange={(val) => formik.setFieldValue("shotlist", val ?? "")}
                        height={500}
                        preview="edit"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                        previewOptions={{ style: { fontFamily: "Montserrat, sans-serif" } }}
                        commands={[
                          commands.title1, commands.title2, commands.title3,
                          commands.divider,
                          commands.bold, commands.italic,
                          commands.divider,
                          commands.unorderedListCommand, commands.orderedListCommand,
                          commands.divider,
                          commands.link,
                        ]}
                        extraCommands={[]}
                      />
                    </div>
                  </Tab>
                )}
                <Tab
                  eventKey="images"
                  title={<TabTitle icon="uil-image" label={t("Backend.Post.Tabs.Images")} />}
                >
                  {postFiles.length > 0 ||
                  (!editData.poll && postFiles.length === 0) ? (
                    <>
                      <Row>
                        <Col className="d-flex justify-content-start">
                          <NewButtonWithIcon
                            className="me-2"
                            title={t("Backend.Post.AddFile")}
                            handleClick={() => {
                              setShowFilemanager(true);
                            }}
                          />
                          <NewButtonWithIcon
                            title={t("Backend.Post.UploadFile")}
                            icon={faUpload}
                            handleClick={() => setShowFileUpload(true)}
                            badge={
                              uploadSummary.active > 0 ? (
                                <span className="uploadBadge">
                                  <FontAwesomeIcon icon={faSpinner} spin /> {uploadSummary.active}
                                </span>
                              ) : uploadSummary.failed > 0 ? (
                                <span className="uploadBadge uploadBadgeFailed">{uploadSummary.failed}</span>
                              ) : null
                            }
                          />
                        </Col>
                      </Row>
                      <hr />

                      <Row>
                        <Col>
                          <DndProvider backend={HTML5Backend}>
                            <Table>
                              <head>
                                <tr>
                                  <td> </td>
                                </tr>
                              </head>
                              <tbody>
                                {postFiles.map((connection, index) => {
                                  return (
                                    <DragableFileListItem
                                      deleteFile={removeFilesFromPost}
                                      file={connection}
                                      index={index}
                                      showPreview={true}
                                      moveRow={moveRow}
                                      dropFunction={dropFunction}
                                      editOverlay={setOverlayConnection}
                                      key={connection.id}
                                    />
                                  );
                                })}
                              </tbody>
                            </Table>
                          </DndProvider>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    t("Backend.Post.Poll.PollSet")
                  )}
                </Tab>

                <Tab
                  eventKey="poll"
                  title={<TabTitle icon="uil-chart-bar" label={t("Backend.Post.Tabs.Poll")} />}
                >
                  {editData.poll ||
                  (!editData.poll && postFiles.length === 0) ? (
                    <>
                      {!editData.poll ? (
                        <Row>
                          <Col>
                            <NewButtonWithIcon
                              className="me-2"
                              title={t("Backend.Post.Poll.AddPoll")}
                              handleClick={() => {
                                addPoll();
                              }}
                            />
                          </Col>
                        </Row>
                      ) : (
                        <>
                          <Row className="mb-5">
                            <Col>
                              <Button
                                className="btn btn-danger"
                                onClick={() => {
                                  deletePoll();
                                }}
                              >
                                {t("Backend.Post.Poll.DeletePoll")}
                              </Button>
                            </Col>
                          </Row>
                          <PollFormView poll={poll} setPoll={setPoll} />
                        </>
                      )}
                    </>
                  ) : (
                    t("Backend.Post.Poll.FilesSet")
                  )}
                </Tab>

                <Tab
                  eventKey="settings"
                  title={<TabTitle icon="uil-cog" label={t("Backend.Post.Tabs.TextSettings", "Text settings")} />}
                >
                  <Row>
                    <Col>
                      <Button
                        type="button"
                        variant={formik.values.no_recycle ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => formik.setFieldValue("no_recycle", !formik.values.no_recycle).then(() => formik.submitForm())}
                      >
                        Exclude from recycling
                      </Button>
                    </Col>
                  </Row>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body style={{ position: "relative", overflow: "hidden" }}>

              {/* AI loading overlay */}
              {aiLoading && (
                <div style={{
                  position: "absolute", inset: 0, zIndex: 20,
                  background: "linear-gradient(135deg, rgba(86,74,177,0.07), rgba(3,142,220,0.07))",
                  backdropFilter: "blur(3px)",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 16,
                  borderRadius: "inherit",
                }}>
                  <div style={{ position: "relative", width: 64, height: 64 }}>
                    <svg width="64" height="64" viewBox="0 0 64 64"
                      style={{ position: "absolute", top: 0, left: 0, animation: "spin-ring 1.2s linear infinite" }}>
                      <defs>
                        <linearGradient id="aiRing" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#564ab1" />
                          <stop offset="100%" stopColor="#038edc" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <circle cx="32" cy="32" r="27" fill="none" stroke="url(#aiRing)" strokeWidth="3" strokeLinecap="round" strokeDasharray="110 50" />
                    </svg>
                    <svg width="64" height="64" viewBox="0 0 64 64" style={{ position: "absolute", top: 0, left: 0 }}>
                      <circle cx="32" cy="32" r="27" fill="none" stroke="#eaecf0" strokeWidth="3" />
                    </svg>
                    <div style={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: 20, color: "#564ab1" }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 14, color: "#111827" }}>
                      {t("Backend.Post.AIworking")}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: "#9299a8" }}>
                      {t("Backend.Modal.LoadingSubtext")}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {[0, 1, 2].map((i) => (
                      <div key={i} style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: "linear-gradient(135deg, #564ab1, #038edc)",
                        animation: `loading-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }} />
                    ))}
                  </div>
                </div>
              )}

              <Tabs
                defaultActiveKey="linkedIn"
                id="uncontrolled-tab-example"
                className="mb-3"
              >
                <Tab
                  eventKey="linkedIn"
                  title={<FontAwesomeIcon icon={faLinkedin} size="2x" />}
                  style={{
                    backgroundColor: "#f4f2ee",
                    paddingBottom: "15px",
                    paddingTop: "15px",
                  }}
                >
                  <LinkedinPreview
                    text={aiPreview ?? previewText}
                    postFiles={postFiles}
                    poll={poll}
                    post={{ title: formik.values.title, text: aiPreview ?? previewText }}
                  />

                  {/* Apply / Discard bar — sits inside the grey area, below the post */}
                  {aiPreview && !aiLoading && (
                    <div style={{
                      margin: "16px 12px 0",
                      padding: "12px 16px",
                      borderRadius: 12,
                      background: "#fff",
                      border: "1px solid rgba(86,74,177,0.2)",
                      boxShadow: "0 2px 12px rgba(86,74,177,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: "#564ab1",
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                        <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: 11 }} />
                        AI Preview
                      </span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => setAiPreview(null)}
                          style={{
                            padding: "5px 14px", borderRadius: 50,
                            border: "1.5px solid #e2e5ea", background: "#f4f2ee",
                            color: "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 5,
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                          {t("Common.Discard")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            formik.setFieldValue("text", aiPreview);
                            setAiPreview(null);
                          }}
                          style={{
                            padding: "5px 14px", borderRadius: 50, border: "none",
                            background: "linear-gradient(135deg,#564ab1,#3d3490)",
                            color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 5,
                            boxShadow: "0 3px 10px rgba(86,74,177,0.3)",
                          }}
                        >
                          <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />
                          {t("Backend.Post.ApplyToPost")}
                        </button>
                      </div>
                    </div>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ComposePostTab;
